import type { ColumnFilterOption } from '@/components/ui';

/**
 * "Sent to client" on invoices — the ONE place its wording, its filter options
 * and its column key are chosen. Owner decision 2026-10-09.
 *
 * ⚠️ **ONE MODULE, SEVERAL SURFACES.** The detail page's badge
 * (`components/documents/SentToClientBadge.tsx`) and the `/invoices` column and
 * funnel all read from here. Two copies would drift on whether the word in the
 * cell means what the word in the filter means. Same split as
 * `lib/imported-from-xero.ts` beside `ImportedFromXeroBadge`.
 *
 * ══ WHAT THE FIELDS MEAN ═══════════════════════════════════════════════════
 *
 *   sentToClient: true,  sentToClientAt: <ts>   Xero accepted an email request
 *                                               made from EMERGE at that time
 *   sentToClient: true,  sentToClientAt: null   Xero itself flags it as sent
 *                                               (sent from inside Xero) — Xero
 *                                               records no date
 *   sentToClient: false                         not known to be sent
 *   sentToClient: undefined                     an older API — UNKNOWN, never
 *                                               "not sent"
 *
 * ⛔ **NEVER CLAIM DELIVERY.** Xero answers an email request with a bare
 * success and nothing anywhere records that a message arrived or was read. No
 * wording here may say "delivered", "received" or "read".
 */

/** The column key the `/invoices` page registers with `useTableFilters`. */
export const SENT_TO_CLIENT_COLUMN = 'sentToClient';

/** The badge's text and the column heading. */
export const SENT_TO_CLIENT_LABEL = 'Sent to client';

const YES = 'Yes';
const NO = 'No';

/**
 * WIRE values — the two strings `queryBoolean()` parses on the server, which
 * `useTableFilters` sends verbatim as the chosen option's `value`.
 */
const SENT = 'true';
const NOT_SENT = 'false';

/**
 * A timestamp is a true instant, so it is shown as the reader's LOCAL date.
 * (`formatDateOnly` slices the UTC date part, which is right for date-only
 * columns and a day off here for anybody east or west of UTC near midnight.)
 */
export function formatSentToClientDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * The badge / tooltip sentence. Returns `null` when the invoice is not known to
 * be sent OR the field is absent (older API) — so a caller cannot render a
 * claim from an unanswered question.
 */
export function sentToClientTitle(
  sent: boolean | undefined,
  sentAt: string | null | undefined,
): string | null {
  if (sent !== true) return null;
  if (sentAt === null || sentAt === undefined) {
    return 'Xero marked this invoice as sent to the client. Xero does not record when.';
  }
  return `Xero marked this invoice as sent to the client on ${formatSentToClientDate(sentAt)}.`;
}

/**
 * The column's `get` — the wire value the filter machinery compares and sends.
 * `undefined` (older API) yields `''`, which matches neither option rather than
 * being counted as "No".
 */
export function sentToClientValue(sent: boolean | undefined): string {
  if (sent === true) return SENT;
  if (sent === false) return NOT_SENT;
  return '';
}

/** The column's `format` — wire value back to the word. Unknown renders unchanged. */
export function sentToClientFormat(value: string): string {
  if (value === SENT) return YES;
  if (value === NOT_SENT) return NO;
  return value;
}

/**
 * The complete option list for the header funnel. `?sentToClient=false`
 * genuinely narrows, so both options are real. Single value only — no `multi`.
 */
export const SENT_TO_CLIENT_FILTER_OPTIONS: ColumnFilterOption[] = [
  { value: SENT, label: YES },
  { value: NOT_SENT, label: NO },
];
